import React, { useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { EmailBody as UiEmailBody, EmailBodyProps as UiEmailBodyProps } from "@mapmycustomers/ui";

import EmailBoilerplate from "@app/component/email/creation/EmailBoilerplate";
import { downloadEmailAttachedFile } from "@app/store/email/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./EmailBody.module.scss";
import EmailContext from "./EmailContext";

type EmailBodyProps = Pick<
  UiEmailBodyProps,
  "action" | "aiWriterVisible" | "className" | "editableRenderer" | "footer"
> & {
  // Withholds callbacks the context always supplies, so a caller can hide gated buttons.
  hideFileAttach?: boolean;
  hidePreview?: boolean;
  hideResourceLink?: boolean;
} & typeof mapDispatchToProps;

const EmailBody = ({
  action,
  className,
  editableRenderer: parentEditableRenderer,
  footer,
  hideFileAttach,
  hidePreview,
  hideResourceLink,
  onFileDownload,
}: EmailBodyProps) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const {
    aiWriterVisible,
    attachedFiles,
    body,
    dynamicVariables,
    dynamicVariablesSupported,
    emailFilled,
    entities,
    entityType,
    loading,
    onAiWriterToggle,
    onAttachedFileDelete,
    onChange,
    onFileAttach,
    onImageUpload,
    onInit,
    onPreviewShow,
    onResourceDownload,
    onResourceLinkInsert,
    onResourceLinksChange,
    onUnsubscribeLinkIncludedChange,
    resourceGetter,
    unsubscribeLinkIncluded,
  } = useNonNullableContext(EmailContext);

  const handleAction = useCallback(
    (analyticKey: string) => {
      analytics.clicked(["Email Action"], { action: analyticKey });
    },
    [analytics]
  );

  const handleAiWriterToggle = useCallback(() => {
    analytics.initiated(["AI Writer Button"]);
    onAiWriterToggle();
  }, [analytics, onAiWriterToggle]);

  const editableRenderer = useCallback<NonNullable<UiEmailBodyProps["editableRenderer"]>>(
    (editable: React.ReactNode) => (
      <>
        {parentEditableRenderer ? parentEditableRenderer(editable) : editable}
        {!aiWriterVisible && <EmailBoilerplate className={styles.boilerplate} />}
      </>
    ),
    [aiWriterVisible, parentEditableRenderer]
  );

  return (
    <UiEmailBody
      action={action}
      aiWriterVisible={aiWriterVisible}
      attachedFiles={attachedFiles}
      className={className}
      dynamicVariables={dynamicVariablesSupported ? dynamicVariables : undefined}
      editableRenderer={editableRenderer}
      editorContentClassName={aiWriterVisible ? styles.aiWriterEditorContent : undefined}
      entityType={entityType}
      footer={footer}
      loading={loading}
      onAction={handleAction}
      onAiWriterToggle={handleAiWriterToggle}
      onChange={onChange}
      onFileAttach={hideFileAttach ? undefined : onFileAttach}
      onFileDelete={onAttachedFileDelete}
      onFileDownload={(attachedFile) => onFileDownload({ attachedFile })}
      onFilePreview={(attachedFile) => onFileDownload({ attachedFile, preview: true })}
      onImageUpload={onImageUpload}
      onInit={onInit}
      onPreview={hidePreview ? undefined : onPreviewShow}
      onResourceDownload={onResourceDownload}
      onResourceLinkInsert={hideResourceLink ? undefined : onResourceLinkInsert}
      onResourceLinksChange={onResourceLinksChange}
      onUnsubscribeLinkIncludedChange={onUnsubscribeLinkIncludedChange}
      previewDisableText={
        emailFilled && entities.length
          ? undefined
          : intl.formatMessage({
              id: "email.creation.body.previewDisabled",
              defaultMessage: "Fill out subject, body and recipient to preview email.",
              description: "Email preview disabled",
            })
      }
      recipientCount={entities.length}
      resourceGetter={resourceGetter}
      showActions
      unsubscribeLinkIncluded={unsubscribeLinkIncluded}
      value={body}
    />
  );
};

const mapDispatchToProps = {
  onFileDownload: downloadEmailAttachedFile,
};

export default connect(null, mapDispatchToProps)(EmailBody);
